import type { GeneratedRoofSystem } from "building-types/roofs/generatedRoofSystemTypes";
import { EAVE_RETURN_RAIL_EPSILON } from "building-types/roofs/roofConstants";
import { WallRoofBaseBoardFilter } from "building-types/settings/settingsTypesEnum";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { Table } from "common/data/table";
import type { ClipRailContext } from "geom/bspClipping";
import { clippingNodeNew, clipRail3 } from "geom/bspClipping";
import type { PolyLine3 } from "geom/polyLine3";
import { buildWallBaseBoardRails } from "../wallBaseBoards";

export function buildRoofToWallRails(
  roof: GeneratedRoofSystem,
  walls: Table<WallSystem>,
  isHorizontal: boolean,
): PolyLine3[] {
  const roofToWallRails: PolyLine3[] = [];
  const wallRoofBaseBoardFilter = isHorizontal
    ? WallRoofBaseBoardFilter.HORIZONTAL_EDGES
    : WallRoofBaseBoardFilter.SLOPED_EDGES;

  roofToWallRails.push(...buildRoofWallBaseBoardRails(roof, walls, wallRoofBaseBoardFilter));

  // handle eave return rails
  if (isHorizontal) {
    const { roofs: subRoofs, clipContext } = roof;
    const allSubRoofs = Object.values(subRoofs);
    const returnWallRails: PolyLine3[] = [];

    for (let i = 0, l = allSubRoofs.length; i < l; i++) {
      returnWallRails.push(...allSubRoofs[i].rails.returnPeakRails);
    }

    const railOptions: ClipRailContext = {
      clippingNodes: [clippingNodeNew(clipContext.roofsBSP)],
      coplanarsToFront: true,
      epsilon: EAVE_RETURN_RAIL_EPSILON,
    };

    returnWallRails.forEach((inputRail) => {
      roofToWallRails.push(
        ...clipRail3({
          rail: inputRail,
          options: railOptions,
        }),
      );
    });
  }

  return roofToWallRails;
}

function buildRoofWallBaseBoardRails(
  roof: GeneratedRoofSystem,
  walls: Table<WallSystem>,
  wallRoofBaseBoardFilter?: WallRoofBaseBoardFilter,
): PolyLine3[] {
  const finalWallBaseBoardRails: PolyLine3[] = [];

  const { roofs: subRoofs, clipContext } = roof;

  for (const subRoofId in subRoofs) {
    const subRoof = !!wallRoofBaseBoardFilter
      ? { ...subRoofs[subRoofId], wallRoofBaseBoardFilter }
      : subRoofs[subRoofId];

    const { wallBaseBoardRails } = buildWallBaseBoardRails({
      subRoof,
      subRoofs,
      roofTopFaces: subRoof.roofTopFaces,
      walls,
      clipContext,
    });

    for (const { rails } of wallBaseBoardRails) {
      finalWallBaseBoardRails.push(...rails);
    }
  }

  return finalWallBaseBoardRails;
}
